"use client";

import { createImageSizes, createImageUrl } from "@/utils/image";
import classNames from "classnames";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { Content } from "@/services/content/types";
import AiLabel from "../ai-label";
import Icon from "../icon";

type SliderProps = {
  images?: {
    url: string;
    alt?: string;
    title?: any;
    photographer?: string;
    asset?: Content.Asset;
  }[];
};

const Slider = ({ images }: SliderProps) => {
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [currentSlide, setCurrentslide] = useState(0);
  const [screenWidth, setScreenWidth] = useState(0);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    const debounceFunction = (cb: () => void, delay: number) => {
      let timer: ReturnType<typeof setTimeout>;
      return () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => cb(), delay);
      };
    };
    const debounce = debounceFunction(handleResize, 150);
    window.addEventListener("resize", debounce);
    return () => {
      window.removeEventListener("resize", debounce);
    };
  }, []);

  useEffect(() => {
    const currentRef = sliderRef.current;
    const itemWidth = currentRef?.children.item(0)?.clientWidth || 0;
    const width = currentRef?.offsetWidth || 0;
    const margin = (width - itemWidth) / 2;

    const options = {
      root: null,
      rootMargin: `0px -${margin > 0 ? margin : 0}px 0px -${
        margin > 0 ? margin : 0
      }px`,
      threshold: [0.51],
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (entry.target.getAttribute("data-id") == null) return;
          setCurrentslide(
            parseInt(entry.target.getAttribute("data-id") || "0"),
          );
        }
      });
    }, options);

    if (currentRef) {
      Array.from(currentRef?.children).forEach((child) => {
        observer.observe(child);
      });
    }

    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) {
        Array.from(currentRef?.children).forEach((child) => {
          observer.unobserve(child);
        });
      }
    };
  }, [sliderRef, screenWidth]);

  const scrollOptions: ScrollIntoViewOptions = {
    behavior: "smooth",
    inline: "center",
    block: "nearest",
  };

  const prev = () => {
    if (currentSlide === 0) return;
    sliderRef.current?.children[currentSlide - 1].scrollIntoView(scrollOptions);
  };

  const next = () => {
    if (currentSlide === (sliderRef.current?.children.length || 0) - 1) return;
    sliderRef.current?.children[currentSlide + 1].scrollIntoView(scrollOptions);
  };

  if (!images) return null;
  return (
    <div>
      <div
        className={classNames("relative", {
          "scroll-snap-type-x space-x-3 lg:space-x-6 flex w-auto hide-scrollbar snap-x snap-mandatory overflow-x-auto px-[4vw] lg:px-[calc(50vw-400px)]":
            images.length > 1,
        })}
        ref={sliderRef}
      >
        {images
          .filter((img) => img?.url)
          .map((image, index) => (
            <div
              key={index}
              className={classNames("lg:w-[960px]", {
                "snap-center shrink-0 w-[90vw]": images.length > 1,
                " m-auto max-w-[960px]": images?.length < 2,
              })}
              data-id={index}
            >
              <div className="aspect-w-16 aspect-h-9 flex justify-end align-bottom 2">
                <Image
                  fill
                  loading="eager"
                  priority
                  rel="preload"
                  quality={90}
                  alt={image.alt || ""}
                  src={createImageUrl(image.url, { crop: "16:9" })}
                  sizes={createImageSizes({
                    mobile: "100vw",
                    tablet: "100vw",
                    desktop: "100vw",
                  })}
                />
                <AiLabel image={image.asset} />
                <div
                  className={
                    "bg-black pointer-events-none transition-opacity " +
                    (currentSlide != index ? "opacity-60" : "opacity-0")
                  }
                />
              </div>
              {(image.title || image.photographer) && (
                <figcaption
                  className={classNames(
                    "!mt-0 bg-sand-600  p-4 text-gray-500 opacity-1 text-sm",
                    {
                      "opacity-60": currentSlide != index,
                    },
                  )}
                >
                  {image.title} {image.photographer}
                </figcaption>
              )}
            </div>
          ))}
      </div>
      {images.length > 1 && (
        <nav className="flex justify-center space-x-4 -translate-y-1/2">
          <button
            aria-label="Föregående bild"
            disabled={currentSlide === 0}
            className={classNames(
              "rounded-full h-9 w-9 items-center flex justify-center text-gray-200",
              {
                "bg-sand-600": currentSlide === 0,
                "bg-sand-700": currentSlide != 0,
                "text-sand-700": currentSlide === 0,
              },
            )}
            onClick={prev}
          >
            <Icon.AngleLeft />
          </button>
          <button
            aria-label="Nästa bild"
            disabled={currentSlide === images.length - 1}
            className={classNames(
              "rounded-full h-9 w-9 items-center flex justify-center text-gray-200",
              {
                "bg-sand-600": currentSlide === images.length - 1,
                "bg-sand-700": currentSlide != images.length - 1,
                "text-sand-700": currentSlide === images.length - 1,
              },
            )}
            onClick={next}
          >
            <Icon.AngleRight />
          </button>
        </nav>
      )}
    </div>
  );
};

export default Slider;
